import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import axios from 'axios';

import {
  toPlotted as plotValue,
  toReal as realValue,
  formatValue as format,
  hasBarValue,
  getScale,
  clampToScale,
  barValue,
} from './barValues';
import { getLoadingPlaces, filterWeeksByLoadingPlace } from './loadingPlaces';
import PeriodGauges from './PeriodGauges';
import Chart from 'react-apexcharts';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';

import { GlobalContext } from '../../store/Provider';
import PageSpinner from '../layout/PageSpinner';

const AVERAGE_COLOR = '#000';
const DELIVERY_COLOR = '#d2043b';

// Above this many bars the value labels start to overlap, so they are dropped.
const MAX_BARS_WITH_LABELS = 18;

/**
 * The six values the webservice returns per week, both on the average and on
 * every delivery. `decimals` only drives how the value is printed; `min` and
 * `max` are the y scale, with a tick every `step` (see getScale).
 */
const METRICS = [
  { field: 'DEGEMMB', labelKey: 'benchmarking.graphLabels.AvgMbiAfi', decimals: 2, min: 1, max: 5, step: 1 },
  { field: 'DEGEMSD', labelKey: 'benchmarking.graphLabels.AvgBaconThickness', decimals: 1, min: 5, max: 20, step: 5 },
  { field: 'DEGEMVD', labelKey: 'benchmarking.graphLabels.AvgMeatThickness', decimals: 1, min: 40, max: 80, step: 10 },
  { field: 'DEGEMGW', labelKey: 'benchmarking.graphLabels.AvgWeight', decimals: 1, min: 80, max: 120, step: 10 },
  // The toeslagen start at 0 and only go below it when the period is negative.
  { field: 'DEGEMKT', labelKey: 'individualBenchmarking.graphLabels.AvgQualitySupplement', decimals: 3, min: 0, max: 0.08, step: 0.01, belowZero: true },
  { field: 'DEGEMGT', labelKey: 'individualBenchmarking.graphLabels.AvgWeightSupplement', decimals: 3, min: 0, max: 0.05, step: 0.01, belowZero: true },
];

// Set `hidden: true` on a metric above to take its graph off the page.
const VISIBLE_METRICS = METRICS.filter(metric => !metric.hidden);

/**
 * Default period: the last 15 weeks, the same window the benchmarking page
 * opens on. The webservice rejects a week outside 1-53, so week 0 is clamped.
 */
const getDefaultPeriod = () => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
  const currentWeek = Math.max(1, Math.ceil((now.getDay() + 1 + days) / 7) - 1);

  let fromWeek = currentWeek - 15;
  let fromYear = now.getFullYear();
  if (fromWeek < 1) {
    fromWeek += 53;
    fromYear -= 1;
  }

  return {
    fromWeek: fromWeek,
    fromYear: fromYear,
    toWeek: currentWeek,
    toYear: now.getFullYear(),
  };
};

const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * The tooltip is built by hand because it shows where a bar comes from, which
 * the default tooltip has no room for. `rows` are [label, value] pairs; a value
 * the webservice left empty shows as a dash.
 */
const renderTooltipHtml = (title, subtitle, rows) => {
  const body = rows
    .map(([label, value]) => {
      const shown =
        value === null || value === undefined || value === '' ? '-' : value;
      return `<div style="display:flex;justify-content:space-between;gap:12px">
          <span style="color:#6c757d">${escapeHtml(label)}</span>
          <strong>${escapeHtml(shown)}</strong>
        </div>`;
    })
    .join('');

  return `<div style="padding:8px 12px;font-size:12px;line-height:1.6;min-width:180px">
      <div style="font-weight:600;margin-bottom:4px">${escapeHtml(title)}</div>
      <div style="color:#6c757d;margin-bottom:6px">${escapeHtml(subtitle)}</div>
      ${body}
    </div>`;
};

const WeeklyBarChart = ({ metric, weeks }) => {
  const { t } = useTranslation();

  // The week with the most deliveries decides how many delivery bars a segment holds.
  const maxDeliveries = weeks.reduce(
    (max, week) => Math.max(max, week.deliveries.length),
    0
  );

  // ApexCharts draws every column from the zero line. The bars have to grow
  // from scale.base instead - the bottom of the scale, or zero on the toeslag
  // graphs so a negative value hangs below it - so the values are plotted as
  // "how far from scale.base" and the axis, the data labels and the tooltip
  // put the real number back. See barValues.js.
  const scale = getScale(metric, weeks);
  // A value off the scale is drawn up to its edge; see clampToScale.
  const toPlotted = value => plotValue(clampToScale(value, scale), scale.base);
  const toReal = plotted => realValue(plotted, scale.base);

  // Series 1 is the week average and is drawn black, so it is the first bar of
  // every segment. Every following series is one delivery of that week; weeks
  // with fewer deliveries get `null` so the bars stay aligned with their week.
  const series = [
    {
      name: t('individualBenchmarking.averageLabel'),
      data: weeks.map(week =>
        toPlotted(week.average ? week.average[metric.field] : null)
      ),
    },
    ...Array.from({ length: maxDeliveries }, (_, index) => ({
      name: t('individualBenchmarking.seriesLabel', { number: index + 1 }),
      data: weeks.map(week =>
        toPlotted(
          index < week.deliveries.length ? week.deliveries[index][metric.field] : null
        )
      ),
    })),
  ];

  const formatValue = value => format(value, metric.decimals);

  // Used where ApexCharts hands back a plotted value (the axis ticks).
  const formatPlotted = plotted => formatValue(toReal(plotted));

  const options = {
    colors: [AVERAGE_COLOR, ...Array(maxDeliveries).fill(DELIVERY_COLOR)],
    chart: {
      id: `individual-benchmarking-${metric.field}`,
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    // Transparent border on every bar - this is what puts white space between
    // the bars inside one week.
    stroke: {
      show: true,
      width: 6,
      colors: ['transparent'],
    },
    // ApexCharts fills bars at 0.85 opacity by default, which washes the red
    // out next to the solid .btn-primary red. Full opacity keeps them equal.
    fill: {
      opacity: 1,
    },
    dataLabels: {
      enabled: weeks.length * (maxDeliveries + 1) <= MAX_BARS_WITH_LABELS,
      offsetY: -22,
      // A week only labels the deliveries it actually has. The series run to
      // the busiest week in the range, so every other week is asked for
      // deliveries it never made; those are left blank rather than drawn as a
      // value. Read from the week itself rather than trusting the plotted
      // value, which the chart may hand back as 0 instead of null.
      formatter: (plotted, { seriesIndex, dataPointIndex }) => {
        const week = weeks[dataPointIndex];
        if (!week) {
          return '';
        }
        if (!hasBarValue(week, seriesIndex)) {
          return '';
        }
        // The real value, not the plotted one, which stops at the scale's edge.
        return formatValue(barValue(week, seriesIndex, metric.field));
      },
      style: {
        fontSize: '12px',
        colors: ['#212529'],
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        const week = weeks[dataPointIndex];
        if (!week) {
          return '';
        }
        const title = `${t('individualBenchmarking.weekLabel', {
          week: week.week,
        })} - ${week.year}`;

        // Series 1 is the week average: it is not one delivery, so it has no
        // loading place, feed type or genetics to show.
        if (seriesIndex === 0) {
          return renderTooltipHtml(
            title,
            t('individualBenchmarking.averageLabel'),
            [[t(metric.labelKey), formatValue(week.average?.[metric.field])]]
          );
        }

        const delivery = week.deliveries[seriesIndex - 1];
        if (!delivery) {
          return '';
        }

        return renderTooltipHtml(
          title,
          t('individualBenchmarking.seriesLabel', { number: seriesIndex }),
          [
            [t(metric.labelKey), formatValue(delivery[metric.field])],
            [t('loadingplaces.code'), delivery.DELPLCE],
            [t('individualBenchmarking.tooltip.feedType'), delivery.DEVOTCE],
            [t('individualBenchmarking.tooltip.genetics'), delivery.DEGENCE],
          ]
        );
      },
    },
    grid: {
      borderColor: '#e0e0e0',
    },
    xaxis: {
      // No axis title: every category already reads "Week 34", "Week 36", ...
      categories: weeks.map(week =>
        t('individualBenchmarking.weekLabel', { week: week.week })
      ),
    },
    yaxis: {
      // The plotted values are offsets from scale.base; the formatter turns
      // every tick back into the real number, so the axis still reads
      // scale.min .. scale.max.
      min: scale.min - scale.base,
      max: scale.max - scale.base,
      tickAmount: scale.tickAmount,
      labels: {
        formatter: formatPlotted,
      },
    },
  };

  return (
    <Card>
      <CardBody>
        <CardTitle>{t(metric.labelKey)}</CardTitle>

        <div className='mixed-chart'>
          <Chart options={options} series={series} type='bar' width='100%' height='380' />
        </div>

        <small className='text-muted'>
          <span style={{ color: AVERAGE_COLOR }}>&#9632;</span>{' '}
          {t('individualBenchmarking.averageLabel')}
          {'  '}
          <span style={{ color: DELIVERY_COLOR }}>&#9632;</span>{' '}
          {t('individualBenchmarking.deliveriesLabel')}
        </small>
      </CardBody>
    </Card>
  );
};

const IndividualBenchmarkingComponent = () => {
  const { t } = useTranslation();
  const { suppliersState } = useContext(GlobalContext);
  const activeID = suppliersState.suppliers.activeID;

  const defaultPeriod = getDefaultPeriod();
  const [filters, setFilters] = useState(defaultPeriod);
  const [period, setPeriod] = useState(defaultPeriod);

  const [weeks, setWeeks] = useState([]);
  // Starts out as every loading place in the period, see fetchIndividualData.
  const [selectedLoadingPlaces, setSelectedLoadingPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchIndividualData = useCallback(async () => {
    if (!activeID) {
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `/jsonapi/benchmarking/individual?supplierid=${activeID}&from_year=${period.fromYear}&from_week=${period.fromWeek}&to_year=${period.toYear}&to_week=${period.toWeek}`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setWeeks(data);
      // A new period comes with its own loading places, all selected again.
      setSelectedLoadingPlaces(getLoadingPlaces(data));
      setError(null);
    } catch (err) {
      setWeeks([]);
      setSelectedLoadingPlaces([]);
      // The webservice sends a readable message of its own on 400 and 403.
      setError(err.response?.data?.error || t('general.somethingWentWrong'));
    }
    setLoading(false);
  }, [activeID, period, t]);

  useEffect(() => {
    fetchIndividualData();
  }, [fetchIndividualData]);

  const loadingPlaces = useMemo(() => getLoadingPlaces(weeks), [weeks]);
  const visibleWeeks = useMemo(
    () => filterWeeksByLoadingPlace(weeks, selectedLoadingPlaces),
    [weeks, selectedLoadingPlaces]
  );
  const allLoadingPlacesSelected =
    selectedLoadingPlaces.length === loadingPlaces.length;

  const toggleLoadingPlace = loadingPlace => {
    setSelectedLoadingPlaces(
      selectedLoadingPlaces.includes(loadingPlace)
        ? selectedLoadingPlaces.filter(item => item !== loadingPlace)
        : [...selectedLoadingPlaces, loadingPlace]
    );
  };

  return (
    <Fragment>
      {loading && <PageSpinner msg={t('general.weAreGettingNewData')} />}

      {/* Period selector */}
      <Row>
        <Col lg='6' sm='12' style={{ height: 'calc(100%-30px)' }}>
          <Card>
            <CardTitle className='border-bottom p-3 mb-0'>
              {t('benchmarking.filtering.period')}
            </CardTitle>
            <CardBody>
              <Form
                className='form-material'
                onSubmit={e => {
                  e.preventDefault();
                  setPeriod(filters);
                }}
              >
                <FormGroup>
                  <Label className='text-primary benchmarking-filter-title'>
                    {t('benchmarking.filtering.from')}
                  </Label>
                  <div className='benchmarking-filter-group'>
                    <div className='filter-group-1'>
                      <Label>{t('benchmarking.filtering.week')}</Label>
                      <Input
                        required={true}
                        type='number'
                        min={1}
                        max={53}
                        name='fromWeek'
                        defaultValue={filters.fromWeek}
                        onChange={e => {
                          setFilters({ ...filters, fromWeek: e.target.value });
                        }}
                      />
                    </div>
                    <div className='filter-group-2'>
                      <Label>{t('benchmarking.filtering.year')}</Label>
                      <Input
                        type='number'
                        name='fromYear'
                        defaultValue={filters.fromYear}
                        onChange={e => {
                          setFilters({ ...filters, fromYear: e.target.value });
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label className='text-primary benchmarking-filter-title'>
                    {t('benchmarking.filtering.to')}
                  </Label>
                  <div className='benchmarking-filter-group'>
                    <div className='filter-group-1'>
                      <Label>{t('benchmarking.filtering.week')}</Label>
                      <Input
                        required={true}
                        type='number'
                        min={1}
                        max={53}
                        name='toWeek'
                        defaultValue={filters.toWeek}
                        onChange={e => {
                          setFilters({ ...filters, toWeek: e.target.value });
                        }}
                      />
                    </div>
                    <div className='filter-group-2'>
                      <Label>{t('benchmarking.filtering.year')}</Label>
                      <Input
                        type='number'
                        name='toYear'
                        defaultValue={filters.toYear}
                        onChange={e => {
                          setFilters({ ...filters, toYear: e.target.value });
                        }}
                      />
                    </div>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Button type='submit' className='btn-primary'>
                    {t('benchmarking.filtering.apply')}
                  </Button>
                </FormGroup>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Loading place filter, applied straight away on the loaded data */}
        <Col lg='6' sm='12'>
          <Card>
            <CardTitle className='border-bottom p-3 mb-0'>
              {t('individualBenchmarking.filtering.loadingPlaces')}
            </CardTitle>
            <CardBody>
              {loadingPlaces.length === 0 ? (
                <p className='text-muted mb-0'>
                  {t('individualBenchmarking.noDataInPeriod')}
                </p>
              ) : (
                <Form className='form-material'>
                  <FormGroup check className='mb-2'>
                    <Input
                      type='checkbox'
                      id='loading-place-all'
                      checked={allLoadingPlacesSelected}
                      onChange={() =>
                        setSelectedLoadingPlaces(
                          allLoadingPlacesSelected ? [] : loadingPlaces
                        )
                      }
                    />
                    <Label check for='loading-place-all' className='font-weight-bold'>
                      {t('individualBenchmarking.filtering.allLoadingPlaces')}
                    </Label>
                  </FormGroup>
                  {loadingPlaces.map(loadingPlace => (
                    <FormGroup check inline key={loadingPlace}>
                      <Input
                        type='checkbox'
                        id={`loading-place-${loadingPlace}`}
                        checked={selectedLoadingPlaces.includes(loadingPlace)}
                        onChange={() => toggleLoadingPlace(loadingPlace)}
                      />
                      <Label check for={`loading-place-${loadingPlace}`}>
                        {loadingPlace}
                      </Label>
                    </FormGroup>
                  ))}
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Graphs */}
      {error && (
        <Row>
          <Col sm='12'>
            <Card>
              <CardBody>
                <p className='text-danger mb-0'>{error}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!error && weeks.length === 0 && !loading && (
        <Row>
          <Col sm='12'>
            <Card>
              <CardBody>
                <p className='text-muted mb-0'>
                  {t('individualBenchmarking.noDataInPeriod')}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!error && weeks.length > 0 && visibleWeeks.length === 0 && (
        <Row>
          <Col sm='12'>
            <Card>
              <CardBody>
                <p className='text-muted mb-0'>
                  {t('individualBenchmarking.noLoadingPlaceSelected')}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!error && visibleWeeks.length > 0 && (
        <Row>
          {VISIBLE_METRICS.map(metric => (
            <Col
              key={metric.field}
              sm='12'
              md='12'
              lg='6'
              // Two graphs a row on a laptop; three only from 1600px (col-xlg-4),
              // below that they are too cramped to read.
              className='col-xlg-4'
            >
              <WeeklyBarChart metric={metric} weeks={visibleWeeks} />
            </Col>
          ))}
        </Row>
      )}

      {/* The same metrics averaged over the whole period */}
      {!error && visibleWeeks.length > 0 && (
        <PeriodGauges weeks={visibleWeeks} metrics={METRICS} />
      )}
    </Fragment>
  );
};

export default IndividualBenchmarkingComponent;
